import { useTranslation } from 'react-i18next';
import { DepositSbpForm } from './DepositSbpForm';
import { Button, ModalComponentWrapper } from '@/shared/components';
import { cn } from '@/shared/utils';
import { useDepositFormLogic } from '@/shared/lib';
import type { CreateOperationSbpDepositResponse } from '@/features/deposit-sbp';
import { useState } from 'react';
import type { DepositModalBaseProps } from '@/shared/types';
import { useAppSettingsDictionary } from '@/shared/api';

export function SbpModalFooter({
  onSubmit,
  isLoading,
  isContinueButton = false,
}: {
  onSubmit: () => void;
  isLoading: boolean;
  isContinueButton: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Button
      type="submit"
      variant={isContinueButton ? 'secondary' : 'default'}
      size="lg"
      disabled={isLoading}
      loading={isLoading}
      onClick={onSubmit}
      className={cn('w-full')}>
      {isContinueButton
        ? t('depositMethods.continueButton')
        : t('depositMethods.sbp.submitButton')}
    </Button>
  );
}

export function DepositSbpModal({ onBack, title }: DepositModalBaseProps) {
  const { appSettingsData } = useAppSettingsDictionary();

  const [operationData, setOperationData] =
    useState<CreateOperationSbpDepositResponse>();
  const {
    form,
    handleFormSubmit,
    currencyOptions,
    minAmount,
    amountPlaceholder,
    commissionData,
    shouldShowCommission,
    isSubmitInProgressWithoutErrors,
    formError,
    isLoading,
    accountsInfoError,
    firstDepositDataError,
    createOperationDepositError,
    openDepositLink,
  } = useDepositFormLogic<CreateOperationSbpDepositResponse>({
    operationType: 'sbp',
    onCreateDepositSuccess: data => {
      setOperationData(data);
    },
  });

  const submitForm = () => {
    if (operationData) {
      openDepositLink(operationData!.url, onBack);
      return;
    }

    form.handleSubmit(handleFormSubmit)();
  };

  const error =
    formError ||
    firstDepositDataError ||
    accountsInfoError ||
    createOperationDepositError;

  return (
    <ModalComponentWrapper
      title={title}
      onBack={onBack}
      footer={
        <SbpModalFooter
          onSubmit={submitForm}
          isContinueButton={!operationData}
          isLoading={isSubmitInProgressWithoutErrors}
        />
      }>
      <DepositSbpForm
        form={form}
        handleFormSubmit={handleFormSubmit}
        currencyOptions={currencyOptions}
        minAmount={minAmount}
        amountPlaceholder={amountPlaceholder}
        commissionData={commissionData}
        shouldShowCommission={shouldShowCommission}
        formError={!isSubmitInProgressWithoutErrors && error}
        isLoading={isLoading}
        limitsInfo={
          appSettingsData?.value?.depositWithdrawInformation?.sbpLimits
        }
      />
    </ModalComponentWrapper>
  );
}
